import { InfiniteData } from 'react-query';
import produce from 'immer';

import { addPrefixToOfficialId, convertOfficialHiIdToOriginalId, queryClient } from '@/utils';
import { CHANNEL_STREAM_LIST_PREFIX } from '@/views/Channel/hooks/query';

import { FeedArticleDetail, FeedComment, FeedItem, TopOfficialAccount } from '../types';
import { LIST_URL, setQueryFeedDetailInList, setQuoteFeedDetailInList } from './list';
import { setArticleDetailQuery, setArticleQuoteDetailQuery } from './detail';
import { RecommendUserRes } from '../components/RecommendUser/hook';

export type UpdateQueryUsersParams = {
  currentAddress: string;
  userId: string;
  userFollowHandler: (user?: UnionAccount) => void;
};

export const setQueryFeedAllUsersInListByFeedList = ({
  currentAddress,
  userId,
  userFollowHandler,
}: UpdateQueryUsersParams) => {
  // 理论上同一时间，只会存在一个 list 的 cache
  Object.values(LIST_URL).forEach(({ url, resKey }) => {
    queryClient.setQueriesData<InfiniteData<Record<string, FeedArticleDetail[]>> | undefined>(
      [url, currentAddress],
      pre => {
        // 不存在这个列表的时候，直接返回了
        if (!pre) {
          return pre;
        }

        return produce(pre, draft => {
          draft.pages.forEach(x => {
            x[resKey]?.forEach((p: FeedArticleDetail) => {
              if (
                p.article.creator_id === String(userId) ||
                p.article.creator_id === addPrefixToOfficialId(String(userId))
              ) {
                userFollowHandler(p.article.creator);
              }

              (p as FeedItem).repost_list?.forEach(r => {
                if (String(r.user.id) === userId) {
                  userFollowHandler(r.user);
                }
              });
            });
          });

          return draft;
        });
      },
    );
  });
};

export const setQueryFeedAllUsersInListByTopComments = ({
  currentAddress,
  userId,
  userFollowHandler,
}: UpdateQueryUsersParams) => {
  const commentQueryKey = ['/article/comments', currentAddress];

  queryClient.setQueriesData<
    InfiniteData<{ comments: FeedComment[]; total_count: number }> | undefined
  >(commentQueryKey, pre => {
    if (!pre) {
      return pre!;
    }

    return produce(pre, draft => {
      draft?.pages?.forEach(page => {
        page?.comments?.forEach(n => {
          if (n.creator_id === userId || n.creator_id === addPrefixToOfficialId(String(userId))) {
            userFollowHandler(n.creator);
          }
        });
      });
    });
  });
};

export const setQueryFeedAllUsersInListByTopOfficials = ({
  currentAddress,
  userId,
  userFollowHandler,
}: UpdateQueryUsersParams) => {
  const sidebarOfficialsQueryKey = ['feed-sidebar-official', currentAddress];
  if (queryClient.getQueriesData(sidebarOfficialsQueryKey)) {
    queryClient.setQueriesData<{ officials: TopOfficialAccount[] }>(
      sidebarOfficialsQueryKey,
      pre => {
        if (!pre) {
          return pre!;
        }

        return produce(pre, pre => {
          pre.officials.forEach(official => {
            if (String(official.id) === convertOfficialHiIdToOriginalId(String(userId))) {
              userFollowHandler(official);
            }
          });
        });
      },
    );
  }
};

export const setQueryFeedAllUsersInListByArticle = ({
  currentAddress,
  userId,
  userFollowHandler,
}: UpdateQueryUsersParams) => {
  const articleDetailQueryKey = ['/article', currentAddress];

  queryClient.setQueriesData<FeedArticleDetail>(articleDetailQueryKey, pre => {
    if (!pre) {
      return pre!;
    }

    return produce(pre, pre => {
      if (
        pre.article?.creator_id === String(userId) ||
        pre.article?.creator_id === addPrefixToOfficialId(String(userId))
      ) {
        userFollowHandler(pre.article.creator);
      }
    });
  });
};

export const setQueryFeedAllUsersInListByTopContributors = ({
  currentAddress,
  userId,
  userFollowHandler,
}: UpdateQueryUsersParams) => {
  const topContributorsQueryKeys = [
    ['/article/top_contributors', currentAddress],
    ['/feed_trend/top_contributors', currentAddress],
    ['/article/contributor', currentAddress],
    ['/feed_trend/contributor', currentAddress],
  ];

  topContributorsQueryKeys.forEach(contributorsQueryKey => {
    if (queryClient.getQueriesData(contributorsQueryKey)) {
      queryClient.setQueriesData<{ contributors: UnionAccount[] }>(contributorsQueryKey, pre => {
        if (!pre) {
          return pre!;
        }

        return produce(pre, pre => {
          if (pre) {
            pre.contributors?.forEach(contributor => {
              if (contributor.id === userId || String(contributor.id) === String(userId)) {
                userFollowHandler(contributor);
              }
            });
          }
        });
      });
    }
  });
};

export const setStreamTopUsersInList = ({
  currentAddress,
  userId,
  userFollowHandler,
}: UpdateQueryUsersParams) => {
  const topUsersQueryKeys = [
    { key: ['/article/top_depositors', currentAddress], resKey: 'depositors' },
    { key: ['/article/top_earners', currentAddress], resKey: 'earners' },
    { key: ['/article/trusters'], resKey: 'trusters' },
    { key: ['/article/distrusters'], resKey: 'distrusters' },
    { key: ['/article/readers'], resKey: 'readers' },
    { key: ['/article/reposters'], resKey: 'reposters' },
  ];

  topUsersQueryKeys.forEach(({ key, resKey }) => {
    queryClient.setQueriesData<{ [_: string]: UnionAccount[] }>(key, pre => {
      if (!pre) {
        return pre!;
      }

      return produce(pre, pre => {
        if (pre) {
          pre[resKey]?.forEach(x => {
            if (x.id === userId || String(x.id) === String(userId)) {
              userFollowHandler(x);
            }
          });
        }
      });
    });
  });
};

export const setQueryAllFeedDetailByUserId = ({
  currentAddress,
  userId,
  recipe,
}: {
  currentAddress: string;
  userId: string;
  recipe: (article: FeedArticleDetail, url: string) => void;
}) => {
  Object.values(LIST_URL).forEach(({ url, resKey }) => {
    const isExist = queryClient.getQueriesData([url, currentAddress]);
    if (isExist) {
      queryClient.setQueriesData<InfiniteData<Record<string, FeedArticleDetail[]>> | undefined>(
        [url, currentAddress],
        pre => {
          if (!pre) {
            return pre;
          }

          return produce(pre, draft => {
            draft.pages.forEach(x => {
              x[resKey]?.forEach((p: FeedArticleDetail) => {
                if (p.article.creator_id === userId) {
                  recipe(p, url);
                }
              });
            });

            return draft;
          });
        },
      );
    }
  });
};

export const setRecommendUserInList = ({
  currentAddress,
  userId,
  userFollowHandler,
}: UpdateQueryUsersParams) => {
  const recommendUserQueryKey = ['/feed_trend/recommend_users', currentAddress];

  if (queryClient.getQueriesData(recommendUserQueryKey)) {
    queryClient.setQueriesData<RecommendUserRes>(recommendUserQueryKey, pre => {
      if (!pre) {
        return pre!;
      }

      return produce(pre, draft => {
        const record = draft?.users?.find(i => i?.id === userId);
        if (record) {
          userFollowHandler(record);
        }
      });
    });
  }
};

export const setChannelUserInList = ({
  currentAddress,
  userId,
  userFollowHandler,
}: UpdateQueryUsersParams) => {
  const articleDetailQueryKey = [CHANNEL_STREAM_LIST_PREFIX, currentAddress];

  queryClient.setQueriesData<InfiniteData<Record<string, FeedArticleDetail[]>> | undefined>(
    articleDetailQueryKey,
    pre => {
      // 不存在这个列表的时候，直接返回了
      if (!pre) {
        return pre;
      }

      return produce(pre, draft => {
        draft.pages.forEach(x => {
          x.articles?.forEach((p: FeedArticleDetail) => {
            if (
              p.article.creator_id === userId ||
              p.article.creator_id === addPrefixToOfficialId(String(userId))
            ) {
              userFollowHandler(p.article.creator);
            }

            (p as FeedItem).repost_list?.forEach(r => {
              if (String(r.user.id) === userId) {
                userFollowHandler(r.user);
              }
            });
          });
        });

        return draft;
      });
    },
  );
};

export const setProposalVoteStatus = ({
  currentAddress,
  articleId,
  usdValue,
}: {
  currentAddress: string;
  articleId: number;
  usdValue: number;
}) => {
  const recipe = (pre: FeedArticleDetail) => {
    if (pre?.article?.proposal) {
      const isVoter = !pre?.article?.proposal?.is_voter;
      pre.article.proposal.is_voter = isVoter;
      if (isVoter) {
        pre.article.proposal.voter_count += 1;
        pre.article.proposal.usd_value += usdValue;
      } else {
        pre.article.proposal.voter_count -= 1;
        pre.article.proposal.usd_value -= usdValue;
      }
    }
  };
  setQueryAllFeedDetailByArticleId({ currentAddress, articleId, recipe, includeQuote: true });
};

export const setQueryAllFeedDetailByArticleId = ({
  currentAddress,
  articleId,
  recipe,
  includeQuote,
}: {
  currentAddress: string;
  articleId: number;
  recipe: (article: FeedArticleDetail) => void;
  // 有些操作没必要去遍历寻找是否有引用此 quote，因为在 quote card 中不展示，比如 repost、trust
  includeQuote?: boolean;
}) => {
  setArticleDetailQuery(articleId, currentAddress, pre => produce(pre, recipe));
  setQueryFeedDetailInList(currentAddress, articleId, recipe);

  if (includeQuote) {
    setQuoteArticle({
      currentAddress,
      articleId,
      recipe,
    });
  }
};

const setQuoteArticle = ({
  currentAddress,
  articleId,
  recipe,
}: {
  currentAddress: string;
  articleId: number;
  recipe: (article: FeedArticleDetail) => void;
}) => {
  setArticleQuoteDetailQuery(articleId, currentAddress, pre => produce(pre, recipe));
  setQuoteFeedDetailInList(currentAddress, articleId, recipe);
};

export const set24hTopXPEarnedUsersInList = ({
  currentAddress,
  userId,
  userFollowHandler,
}: UpdateQueryUsersParams) => {
  const queryKey = ['/xp/24h_rank_list', currentAddress];

  queryClient.setQueryData<{ [_: string]: UnionAccount[] }>(queryKey, pre => {
    if (!pre) {
      return pre!;
    }
    return produce(pre, draft => {
      if (draft) {
        draft?.rank_list?.forEach(x => {
          if (x.id === userId || String(x.id) === String(userId)) {
            userFollowHandler(x);
          }
        });
      }
    });
  });
};
